import { WHITE } from "ds_legacy/materials/colors";
import { Divider } from "ds_legacy/materials/layouts";
import { dp, Z_INDEX_MAX } from "ds_legacy/materials/metrics";
import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import styled from "styled-components";
import Step from "./Step";
const STEP_WIDTH = 130;
const DOT_WIDTH = 2;
const DOT_BORDER_SIZE = 1;

const StepperContainer = styled.div<{ isFixed?: boolean; isFullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${(props) => (props.isFixed ? "var(--step-menu-height)" : "38px")};
  width: ${(props) => (props.isFullWidth ? "100%" : undefined)};
  left: ${(props) => (props.isFixed && props.isFullWidth ? 0 : undefined)};
  right: ${(props) => (props.isFixed && props.isFullWidth ? 0 : undefined)};
  box-sizing: border-box;
  position: ${(props) => (props.isFixed ? "fixed" : undefined)};
  z-index: ${(props) => (props.isFixed ? Z_INDEX_MAX : undefined)};
  background: ${(props) => (props.isFixed ? WHITE : undefined)};
  cursor: default;
`;

const StepsContainer = styled.div<{ $shouldCenterSteps?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  justify-content: ${(props) =>
    props.$shouldCenterSteps ? "center" : undefined};
`;

const StepContainer = styled.div<{ stepWidth?: number }>`
  min-width: ${(props) => dp(props.stepWidth ? props.stepWidth : STEP_WIDTH)};
  text-align: center;
`;

/** Primary progress segment — `div` so width/left are not overridden by `hr` UA styles. */
const ProgressLine = styled.div`
  align-self: flex-start;
  background-color: ${(props) => (props.theme as any).palette.primary.main};
  box-sizing: border-box;
  display: block;
  flex: 0 0 auto;
  height: 2px;
  margin-top: -2px;
  min-width: 0;
  opacity: 1;
  position: relative;
`;

export const StepMenuContainer = styled.div<{
  center?: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => (props.center ? "center" : undefined)};
  width: 100%;
  box-sizing: border-box;
  @media print {
    display: none;
  }
`;

export const MIN_STEP_WIDTH = 146;

export function normalizeStepWidth(
  refs: Array<any>,
  stepWidth: number | undefined,
) {
  if (refs.length === 0 || !refs[0]) return;
  const widths: Array<number> = [];
  const minWidth = stepWidth
    ? Math.min(stepWidth, MIN_STEP_WIDTH)
    : MIN_STEP_WIDTH;
  refs.forEach((ref) => widths.push(ref.getBoundingClientRect().width));
  const maxWidth = Math.max.apply(null, widths);
  let normalizedWidth = Math.ceil(maxWidth);
  const isEven = normalizedWidth % 2 == 0;
  if (!isEven) normalizedWidth += 1;
  if (normalizedWidth < minWidth) normalizedWidth = minWidth;

  refs.forEach((ref) => (ref.style.width = dp(normalizedWidth)));
}

export default function StepMenu<T>({
  activeStep,
  isFixed,
  isFullWidth,
  renderStep,
  shouldCenterSteps,
  showProgress,
  steps,
  stepWidth,
}: {
  activeStep: number;
  isFixed?: boolean;
  isFullWidth?: boolean;
  shouldCenterSteps?: boolean;
  renderStep?: (step: T, currentStep: number) => React.ReactNode;
  showProgress?: boolean;
  stepWidth?: number;
  steps: Array<T>;
}) {
  const stepRefs = useRef<Array<HTMLDivElement | null>>([]);
  const progressLineRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const adjustedActiveStep = Math.max(
    0,
    Math.min(activeStep, steps.length - 1),
  );

  useEffect(() => {
    if (isFixed && containerRef.current) {
      const height = containerRef.current.offsetHeight;
      document.documentElement.style.setProperty(
        "--step-menu-height",
        `${height}px`,
      );
    }
  }, [isFixed]);

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    const barEl = progressLineRef.current;
    if (!containerEl || !barEl) {
      return;
    }

    const cells = steps
      .map((_, index) => stepRefs.current[index])
      .filter((n): n is HTMLDivElement => n !== null);

    if (cells.length !== steps.length) {
      return;
    }

    normalizeStepWidth(cells, stepWidth);

    const applyBar = () => {
      const containerRect = containerEl.getBoundingClientRect();

      if (showProgress) {
        const firstEl = cells[0];
        const activeEl = cells[adjustedActiveStep];
        const firstRect = firstEl.getBoundingClientRect();
        const activeRect = activeEl.getBoundingClientRect();
        const leftPx = firstRect.left - containerRect.left;
        const spanPx =
          activeRect.left + activeRect.width / 2 - firstRect.left;

        barEl.style.left = dp(leftPx);
        barEl.style.width = dp(Math.max(0, spanPx));
        return;
      }

      const activeRef = cells[adjustedActiveStep];
      const stepsWidth = activeRef.offsetWidth;
      const dotsWidth =
        adjustedActiveStep * (DOT_WIDTH + DOT_BORDER_SIZE * 2);

      barEl.style.width = dp(stepsWidth);

      const tabLeft = shouldCenterSteps
        ? activeRef.offsetLeft
        : cells.reduce((acc, ref, index) => {
            if (index < adjustedActiveStep) {
              return acc + ref.offsetWidth;
            }
            return acc;
          }, 0);

      barEl.style.left = dp(
        shouldCenterSteps ? tabLeft : dotsWidth + tabLeft,
      );
    };

    applyBar();

    if (showProgress && cells[0].getBoundingClientRect().width < 1) {
      requestAnimationFrame(() => {
        requestAnimationFrame(applyBar);
      });
    }
  }, [
    adjustedActiveStep,
    shouldCenterSteps,
    showProgress,
    stepWidth,
    steps.length,
  ]);

  return (
    <>
      <StepperContainer
        ref={containerRef}
        data-testid="step-menu"
        isFixed={isFixed}
        isFullWidth={isFullWidth}
      >
        <StepsContainer $shouldCenterSteps={shouldCenterSteps}>
          {steps.map((step, index) => (
            <StepContainer
              key={index}
              ref={(node) => {
                stepRefs.current[index] = node;
              }}
              stepWidth={stepWidth}
            >
              {renderStep ? (
                renderStep(step, index)
              ) : (
                <Step
                  isActive={index === adjustedActiveStep}
                  isDone={index < adjustedActiveStep}
                >
                  {step as ReactNode}
                </Step>
              )}
            </StepContainer>
          ))}
        </StepsContainer>
        <Divider />
        <ProgressLine ref={progressLineRef} />
      </StepperContainer>
      {isFixed ? <div style={{ height: "var(--step-menu-height)" }} /> : null}
    </>
  );
}
